import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

export default function Nav({ variant = "landing" }) {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  const isAuthed = user && typeof user === "object";

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
          scrolled ? "backdrop-blur-md" : ""
        }`}
        style={{
          background: scrolled ? "rgba(5,8,20,0.72)" : "transparent",
          borderBottom: scrolled ? "1px solid var(--hs-border)" : "1px solid transparent",
        }}
      >
        <div className="container-hs flex items-center justify-between py-5">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
            <BrandMark />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-hs hidden sm:inline pl-3 border-l border-hs">
              Development Review Portal
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            <a href="/#platform" className="hs-link font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs" data-testid="nav-platform">
              Platform
            </a>
            <a href="/#manifesto" className="hs-link font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs" data-testid="nav-manifesto">
              Manifesto
            </a>
            <a href="/#apps" className="hs-link font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs" data-testid="nav-apps">
              Apps
            </a>
            <a href="/#demo" className="hs-link font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs" data-testid="nav-demo">
              Demo
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {isAuthed ? (
              <>
                <Link to="/apps" className="hs-btn" data-testid="nav-apps-portal">
                  Enter Portal
                </Link>
                <button
                  onClick={async () => {
                    await logout();
                    nav("/");
                  }}
                  className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs hs-link"
                  data-testid="nav-logout"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs hs-link" data-testid="nav-login">
                  Sign in
                </Link>
                <Link to="/register" className="hs-btn hs-btn-primary" data-testid="nav-register">
                  Get Access →
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 md:hidden pt-24 px-6"
            style={{ background: "var(--hs-bg)" }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6">
              <a href="/#platform" className="font-display text-4xl">Platform</a>
              <a href="/#manifesto" className="font-display text-4xl">Manifesto</a>
              <a href="/#apps" className="font-display text-4xl">Apps</a>
              <a href="/#demo" className="font-display text-4xl">Demo</a>
              <div className="hs-divider my-4" />
              {isAuthed ? (
                <>
                  <Link to="/apps" className="hs-btn hs-btn-primary w-fit">Enter Portal</Link>
                  <button onClick={logout} className="hs-btn w-fit">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="hs-btn w-fit">Sign in</Link>
                  <Link to="/register" className="hs-btn hs-btn-primary w-fit">Get Access</Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function BrandMark({ size = 32 }) {
  // Official Harsight Technologies wordmark. The asset is dark-on-transparent,
  // so we mount it on a cream lockup for legibility against the dark portal shell.
  return (
    <span
      className="inline-flex items-center rounded-md"
      aria-label="Harsight Technologies"
      style={{
        background: "var(--hs-cream)",
        padding: "6px 10px",
      }}
    >
      <img
        src="/harsight-logo.png"
        alt="Harsight Technologies"
        style={{ height: size, width: "auto", display: "block" }}
        draggable={false}
      />
    </span>
  );
}

// HARS three-bar mark used inside the partnership pill (matches harsrcm.com identity).
export function HarsMark({ size = 42 }) {
  return (
    <span
      className="inline-flex flex-col items-start justify-center rounded-md bg-white px-2.5 py-1.5"
      aria-label="HARS"
    >
      <span className="flex items-end gap-[3px]" aria-hidden>
        <span className="block h-[3px] w-[16px] rounded-full" style={{ background: "#00153D" }} />
        <span className="block h-[3px] w-[16px] rounded-full" style={{ background: "#3F9C35" }} />
        <span className="block h-[3px] w-[16px] rounded-full" style={{ background: "#0A3D6E" }} />
      </span>
      <span
        className="mt-[3px] font-display text-[1rem] font-bold leading-none"
        style={{ color: "#00153D", letterSpacing: "-0.02em" }}
      >
        HARS
      </span>
    </span>
  );
}
